<template>
    <div>
        <div class="p-6 bg-white rounded-xl shadow mx-auto mt-6">
            <h2 class="text-2xl font-semibold text-gray-800 mb-6">
                Quản lý đặt xe
            </h2>

            <hts-data-table
                ref="dataTables"
                :headers="tableHeaders"
                :server="apiLists"
                store-name="bookingDataTable"
                store-key="Booking" />
        </div>

        <booking-detail ref="bookingDetail" />
        <cancel-booking
            ref="cancelBooking"
            @refresh="refreshTable" />
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import BookingDetail from './ignore/booking-detail.vue'
import CancelBooking from './ignore/cancel-booking.vue'
import Status from '~/components/status.vue'
import Actions from '~/pages/trucker-[id]/driver/ignore/actions.vue'

const { $formatters } = useNuxtApp()
definePageMeta({
  layout: 'user'
})

const dataTables = ref()
const bookingDetail = ref()
const cancelBooking = ref()

// API để lấy danh sách booking
const apiLists = { url: '/user/booking/list', method: 'post' }

// Function to view booking detail
const viewBookingDetail = (data: any) => {
  if (bookingDetail.value) {
    bookingDetail.value.open(data)
  }
}

// Function to cancel booking
const openCancelBooking = (data: any) => {
  if (cancelBooking.value) {
    cancelBooking.value.open(data)
  }
}

const validateShowCancel = (data: any) => {
  return data.status === 1
}

// Định nghĩa cấu trúc bảng
const tableHeaders = [
  {
    field: 'bookingNumber',
    headerName: $t('labels.bookingNo'),
    resizable: true,
    sortable: true,
    minWidth: 180,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'contactName',
    headerName: $t('labels.fullName'),
    resizable: true,
    sortable: true,
    minWidth: 180,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'contactPhone',
    headerName: $t('labels.phone'),
    filter: 'agTextColumnFilter'
  },
  {
    field: 'contactEmail',
    headerName: $t('labels.email'),
    filter: 'agTextColumnFilter'
  },
  {
    field: 'originName',
    headerName: $t('labels.origin'),
    filter: 'agTextColumnFilter',
    minWidth: 180
  },
  {
    field: 'destinationName',
    headerName: $t('labels.destination'),
    filter: 'agTextColumnFilter',
    minWidth: 180
  },
  {
    field: 'status',
    headerName: $t('labels.shippingStatus'),
    cellRenderer: Status,
    cellRendererParams: {
      module: 'bookingStatus',
      label: '',
      keyStatus: 'status'
    },
    filter: 'TableFilterStatus',
    filterItems: [
      { name: $t('labels.all'), value: null },
      { name: 'Chờ xác nhận', value: 1, class: { '!bg-gray-100': true, '!text-gray-800': true } },
      { name: 'Chờ chối', value: 0, class: { '!bg-error-100': true, '!text-error-800': true } },
      { name: 'Đã xác nhận', value: 2, class: { '!bg-info-100': true, '!text-info-800': true } },
      { name: 'Đang vận chuyển', value: 3, class: { '!bg-warning-100': true, '!text-warning-800': true } },
      { name: 'Hoàn thành', value: 4, class: { '!bg-success-100': true, '!text-success-800': true } },
      { name: $t('labels.cancel'), value: -1, class: { '!bg-error-100': true, '!text-error-800': true } }
    ],
    filterModelDefault: { type: 'equals', filterType: 'number' }
  },
  {
    field: 'noteStatus',
    headerName: $t('labels.noteStatus'),

    valueFormatter: (params: any) => {
      const status = params?.data?.status
      if (status === 0) {
        return params?.data?.reasonRejectLabel
      }
      if (status === -1) {
        return params?.data?.reasonCancelLabel
      }
      return ''
    }
  },
  {
    field: 'shipmentType',
    headerName: $t('labels.shipmentType'),
    filter: 'agTextColumnFilter',
    valueFormatter: (params: any) => {
      const shipmentType = params?.data?.shipmentType
      if (shipmentType === 'import') {
        return 'Nhập khẩu'
      }
      if (shipmentType === 'export') {
        return 'Xuất khẩu'
      }
      return ''
    }
  },
  {
    field: 'transportation',
    headerName: $t('labels.transportation'),
    filter: 'agTextColumnFilter',
    valueFormatter: (params: any) => {
      if (params?.data?.transportation === 'CONTAINER') {
        return `Container ${params?.data?.containerType || ''}`
      } else {
        return `Xe tải ${params?.data?.weightCargo ? params?.data?.weightCargo + ' kg' : ''}`
      }
    }
  },
  {
    field: 'bookingQuantity',
    headerName: $t('labels.quantity'),
    filter: 'agNumberColumnFilter',
    minWidth: 120
  },
  {
    field: 'price',
    headerName: $t('labels.price'),
    filter: 'agNumberColumnFilter',
    valueFormatter: (params: any) => {
      if (params?.data?.price) {
        return $formatters.formatCurrency(params.data.price)
      }
      return ''
    }
  },
  {
    field: 'price',
    headerName: 'Tổng giá',
    valueFormatter: (params: any) => {
      if (params?.data?.price) {
        return $formatters.formatCurrency(params.data.price * params.data.bookingQuantity)
      }
      return ''
    }
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      if (params.data.createdAt) {
        return $formatters.datetime(params.data.createdAt)
      }
    }
  },
  {
    field: 'updatedAt',
    headerName: $t('labels.updatedAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      if (params.data.updatedAt) {
        return $formatters.datetime(params.data.updatedAt)
      }
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: Actions,
    cellRendererParams: {
      storeKey: 'bookingDataTable',
      storeName: 'Booking',
      items: [
        {
          icon: 'hi-eye-icon',
          label: $t('labels.viewDetail'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: true,
          click: viewBookingDetail
        },
        {
          icon: 'hi-x-circle-icon',
          label: $t('labels.cancel'),
          class: 'text-error-600 hover:text-error-500',
          permissions: true,
          click: openCancelBooking,
          validate: validateShowCancel
        }
      ]
    }
  }
]

// Refresh bảng
const refreshTable = () => {
  if (dataTables.value) {
    dataTables.value.refresh()
  }
}
</script>
